import {
  type FC,
  type ReactNode,
  type CSSProperties,
  type ComponentType,
} from 'react';
import { ArrowLeft, X, PanelRightOpen, PanelRightClose } from 'lucide-react';
import TrashAnimatedIcon from '@/components/ui/TrashAnimatedIcon';
import SaveAnimatedIcon from '@/components/ui/SaveAnimatedIcon';
// Spinner and waiting-state classes for the save button (JOSH-89).
import '@/components/ReconnectingPill.css';
import UndoAnimatedIcon from '@/components/ui/UndoAnimatedIcon';
import SquareCheckAnimatedIcon from '@/components/ui/SquareCheckAnimatedIcon';
import SearchInputRaw from '@/components/ui/SearchInput';
import PeriodFilterRaw from '@/components/ui/PeriodFilter';
import AdvancedFiltersPopupRaw from '@/components/ui/AdvancedFiltersPopup';

// Single-step casts at import boundary: sibling components have stricter (or
// partially inferred) prop shapes — this hub passes through subsets via
// permissive forwarding. Siblings will tighten their own shapes and drop these
// casts without touching this file. Same pattern as AdvancedFiltersPopup's
// own dropdown forwards. Zero `any` introduced.
const SearchInput = SearchInputRaw as unknown as ComponentType<Record<string, unknown>>;
const PeriodFilter = PeriodFilterRaw as unknown as ComponentType<Record<string, unknown>>;
const AdvancedFiltersPopup = AdvancedFiltersPopupRaw as unknown as ComponentType<
  Record<string, unknown>
>;

/**
 * GenericHeader - Componente padrão de header do sistema Joshua
 *
 * Três modos de operação:
 * - LIST: Para telas de listagem (exibe contagem, botão Add)
 * - DETAILS: Para telas de detalhes de um registro (exibe nome do registro, botões Edit/Delete)
 * - DRAWER: Para drawers/painéis laterais (sem busca, sem voltar, com botão X para fechar)
 *
 * No modo DRAWER:
 * - Search desativado por padrão
 * - Sem botão Voltar
 * - Sem botão Add
 * - Botão X para fechar (só em modo view)
 * - Suporta isEditing para alternar entre view/edit
 * - Botões Cancelar/Salvar em modo edit
 */

// -----------------------------------------------------------------------------
// File-local companion types — all optional fields (consumers pass subsets).
// -----------------------------------------------------------------------------

type GenericHeaderMode = 'list' | 'details' | 'drawer';

interface SearchFieldShape {
  field: string;
  label: string;
}

interface SearchPeriodToggleShape {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  show?: boolean;
}

interface PeriodFilterShape {
  value?: string;
  onChange?: (...args: unknown[]) => void;
  options?: string[];
  customPeriod?: { inicio: string; fim: string };
  onCustomPeriodChange?: (...args: unknown[]) => void;
  showNavigation?: boolean;
}

interface AdvancedFiltersShape {
  filters?: unknown[];
  values?: Record<string, unknown>;
  onChange?: (...args: unknown[]) => void;
  data?: unknown[];
  lookups?: Record<string, unknown>;
  distinctValues?: Record<string, unknown>;
}

interface BulkSelectShape {
  enableEdit?: boolean;
  enableDelete?: boolean;
  active?: boolean;
  type?: 'edit' | 'delete' | string;
  selectedCount?: number;
  onEnterEdit?: () => void;
  onEnterDelete?: () => void;
  onExit?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

interface GenericHeaderProps {
  // Modo de operação
  mode?: GenericHeaderMode;

  // Título e subtítulo
  title?: ReactNode;
  count?: number;
  secondaryTitle?: ReactNode;
  badge?: ReactNode;

  // Ações
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onBack?: () => void;
  onClose?: () => void;
  onSave?: () => void;
  onCancel?: () => void;

  // Estado do drawer
  isEditing?: boolean;
  saving?: boolean;
  /** JOSH-89: a save was answered while the session reconnects; Salvar waits. */
  awaitingConnection?: boolean;

  // Permissões (controle de visibilidade dos botões)
  canAdd?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;

  // Labels customizados
  addLabel?: string;

  // Search
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchFields?: SearchFieldShape[];
  searchPlaceholder?: string;
  showSearch?: boolean;
  searchPeriodToggle?: SearchPeriodToggleShape;

  // PeriodFilter (opcional)
  periodFilter?: PeriodFilterShape;
  periodFilterFirst?: boolean;

  // AdvancedFiltersPopup (opcional)
  advancedFilters?: AdvancedFiltersShape;

  // Seleção em Lote (opcional)
  bulkSelect?: BulkSelectShape;

  // Drawer expand/collapse
  isExpanded?: boolean;
  onToggleExpand?: () => void;

  // Extras
  className?: string;
  children?: ReactNode;
}

const GenericHeader: FC<GenericHeaderProps> = ({
  // Modo de operação
  mode = 'list', // 'list' | 'details' | 'drawer'

  // Título e subtítulo
  title,
  count, // Para modo list - exibe "(count)"
  secondaryTitle, // Para modo details/drawer - exibe nome do registro
  badge, // Elemento React para exibir ao lado do título (ex: status badge)

  // Ações
  onAdd, // Modo list - botão + Novo
  onEdit, // Modo details/drawer - botão Edit
  onDelete, // Modo details/drawer - botão Delete
  onBack, // Modo list/details - botão Voltar
  onClose, // Modo drawer - botão X para fechar
  onSave, // Modo drawer (edit) - botão Salvar
  onCancel, // Modo drawer (edit) - botão Cancelar

  // Estado do drawer
  isEditing = false, // Modo drawer - se está editando
  saving = false, // Modo drawer - se está salvando
  awaitingConnection = false, // Modo drawer - salvar aguarda a sessão reconectar

  // Permissões (controle de visibilidade dos botões)
  canAdd = true,
  canEdit = true,
  canDelete = true,

  // Labels customizados
  addLabel = 'Novo',

  // Search
  searchValue,
  onSearchChange,
  searchFields = [], // Array de { field: 'path.to.field', label: 'Label' }
  searchPlaceholder,
  showSearch = true,
  searchPeriodToggle, // { checked, onChange, show } - toggle "Todo período" dentro do search

  // PeriodFilter (opcional) - passa objeto com props do PeriodFilter
  // { value, onChange, options, customPeriod, onCustomPeriodChange, showNavigation }
  periodFilter,
  periodFilterFirst = false, // When true, renders PeriodFilter before AdvancedFilters (left-aligned)

  // AdvancedFiltersPopup (opcional) - passa objeto com props do AdvancedFiltersPopup
  // { filters, values, onChange, data, lookups }
  advancedFilters,

  // Seleção em Lote (opcional) - passa objeto com props de bulk select
  // { enableEdit, enableDelete, active, type, selectedCount, onEnterEdit, onEnterDelete, onExit, onEdit, onDelete }
  bulkSelect,

  // Drawer expand/collapse
  isExpanded = false, // Modo drawer - se está expandido
  onToggleExpand, // Modo drawer - callback para toggle expand

  // Extras
  className = '',
  children, // Para ações customizadas adicionais
}) => {
  const isListMode = mode === 'list';
  const isDetailsMode = mode === 'details';
  const isDrawerMode = mode === 'drawer';

  // Determina o texto secundário baseado no modo
  const getSecondaryText = (): ReactNode => {
    if (
      isListMode &&
      count !== undefined &&
      typeof title === 'string' &&
      title.trim()
    ) {
      return `(${count})`;
    }
    if ((isDetailsMode || isDrawerMode) && secondaryTitle) {
      return secondaryTitle;
    }
    return null;
  };

  const secondaryText = getSecondaryText();

  // No modo drawer, desabilitar search por padrão
  const shouldShowSearch = isDrawerMode ? false : showSearch && Boolean(onSearchChange);

  const titleGroupStyle: CSSProperties | undefined = isDetailsMode
    ? { marginRight: 'auto' }
    : isListMode && (shouldShowSearch || advancedFilters || periodFilter)
      ? { flex: 'none' } // Não expande quando há filtros centralizados
      : undefined;

  return (
    <header data-component="generic-header" className={`ac-header ${className}`}>
      {/* Botão Voltar - à esquerda do título */}
      {!isDrawerMode && onBack && (
        <button
          onClick={onBack}
          title="Voltar"
          style={{
            background: 'none',
            border: 'none',
            padding: '4px',
            cursor: 'pointer',
            color: '#9ca3af',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '8px',
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#000000')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#9ca3af')}
        >
          <ArrowLeft size={20} />
        </button>
      )}

      {/* Botão Expandir/Recolher - modo drawer, à esquerda do título */}
      {isDrawerMode && onToggleExpand && (
        <button
          onClick={onToggleExpand}
          title={isExpanded ? 'Recolher drawer' : 'Expandir drawer'}
          style={{
            background: 'none',
            border: 'none',
            padding: '4px',
            cursor: 'pointer',
            color: '#9ca3af',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '8px',
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#154680')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#9ca3af')}
        >
          {isExpanded ? <PanelRightClose size={18} /> : <PanelRightOpen size={18} />}
        </button>
      )}

      {/* Título + Badge + Texto Secundário */}
      <div className="ac-header__title-group" style={titleGroupStyle}>
        <h2 className="ac-title" style={isDrawerMode ? { fontSize: '14px' } : undefined}>
          {typeof title === 'string' ? title.toUpperCase() : title}
        </h2>
        {badge}
        {secondaryText && <span className="ac-header__secondary">{secondaryText}</span>}
      </div>

      {/* Grupo central: Filtros + Search + Período */}
      {!isDrawerMode && (shouldShowSearch || advancedFilters || periodFilter) && (
        <div
          className="ac-header__search-group"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: periodFilterFirst ? 'flex-start' : 'center',
            flex: 1,
            gap: '8px',
            overflow: 'visible',
          }}
        >
          {/* PeriodFilter — rendered first when periodFilterFirst */}
          {periodFilterFirst && periodFilter && (
            <div style={{ position: 'relative', zIndex: 1000 }}>
              <PeriodFilter
                value={periodFilter.value}
                onChange={periodFilter.onChange}
                options={periodFilter.options}
                customPeriod={periodFilter.customPeriod}
                onCustomPeriodChange={periodFilter.onCustomPeriodChange}
                showNavigation={periodFilter.showNavigation !== false}
                compact
              />
            </div>
          )}

          {/* AdvancedFiltersPopup */}
          {advancedFilters && (
            <AdvancedFiltersPopup
              filters={advancedFilters.filters}
              values={advancedFilters.values}
              onChange={advancedFilters.onChange}
              data={advancedFilters.data}
              lookups={advancedFilters.lookups}
              distinctValues={advancedFilters.distinctValues}
            />
          )}

          {/* Campo de Busca - cresce/encolhe conforme espaço disponível */}
          {shouldShowSearch && (
            <div
              className="ac-header__search"
              style={{
                flex: 1,
                maxWidth: '440px',
                padding: '0 20px',
              }}
            >
              <SearchInput
                value={searchValue}
                onChange={onSearchChange}
                fields={searchFields}
                placeholder={searchPlaceholder}
                periodToggle={searchPeriodToggle}
              />
            </div>
          )}

          {/* PeriodFilter — default position (right) when not periodFilterFirst */}
          {!periodFilterFirst && periodFilter && (
            <div style={{ position: 'relative', zIndex: 1000 }}>
              <PeriodFilter
                value={periodFilter.value}
                onChange={periodFilter.onChange}
                options={periodFilter.options}
                customPeriod={periodFilter.customPeriod}
                onCustomPeriodChange={periodFilter.onCustomPeriodChange}
                showNavigation={periodFilter.showNavigation !== false}
                compact
              />
            </div>
          )}
        </div>
      )}

      {/* Grupo direito: ações */}
      <div className="ac-header__right">
        {/* Ações */}
        <div className="ac-header__actions">
          {/* === BULK SELECT: Quando modo bulk está ATIVO === */}
          {isListMode && bulkSelect?.active && (
            <>
              {/* Contador de selecionados */}
              {(bulkSelect.selectedCount ?? 0) > 0 && (
                <span
                  style={{
                    fontSize: '13px',
                    color: 'var(--color-primary)',
                    fontWeight: 500,
                    marginRight: '8px',
                  }}
                >
                  {bulkSelect.selectedCount} selecionado
                  {bulkSelect.selectedCount !== 1 ? 's' : ''}
                </span>
              )}
              {/* Botão Cancelar */}
              <button className="btn-add undo-animated" onClick={bulkSelect.onExit}>
                <UndoAnimatedIcon size={15} /> <span className="btn-text">Cancelar</span>
              </button>
              {/* Botão Editar - quando type é 'edit' */}
              {bulkSelect.type === 'edit' && (
                <button
                  className="btn-add gf-pen-animated"
                  onClick={bulkSelect.onEdit}
                  disabled={bulkSelect.selectedCount === 0}
                  style={{
                    opacity: bulkSelect.selectedCount === 0 ? 0.5 : 1,
                    cursor: bulkSelect.selectedCount === 0 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <defs>
                      <linearGradient
                        id="bulkPenG"
                        gradientUnits="userSpaceOnUse"
                        x1="3"
                        y1="0"
                        x2="21"
                        y2="0"
                      >
                        <stop offset="0%" stopColor="#60a5fa" />
                        <stop offset="100%" stopColor="#154680" />
                      </linearGradient>
                    </defs>
                    <g className="pen-square" stroke="url(#bulkPenG)">
                      <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    </g>
                    <g className="pen-tip" stroke="url(#bulkPenG)">
                      <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" />
                    </g>
                  </svg>
                  <span className="btn-text">Editar</span>
                </button>
              )}
              {/* Botão Excluir - quando type é 'delete' */}
              {bulkSelect.type === 'delete' && (
                <button
                  className="btn-add gf-trash-animated"
                  onClick={bulkSelect.onDelete}
                  disabled={bulkSelect.selectedCount === 0}
                >
                  <TrashAnimatedIcon size={15} /> <span className="btn-text">Excluir</span>
                </button>
              )}
            </>
          )}

          {/* === BULK SELECT: Botões de ativação (quando NÃO está ativo) === */}
          {isListMode && !bulkSelect?.active && (
            <>
              {/* Botão de Edição em Lote */}
              {bulkSelect?.enableEdit && (
                <button
                  className="btn-bulk-action-icon check-animated"
                  onClick={bulkSelect.onEnterEdit}
                  title="Editar em Lote"
                >
                  <SquareCheckAnimatedIcon size={18} />
                </button>
              )}
              {/* Botão de Exclusão em Lote */}
              {bulkSelect?.enableDelete && (
                <button
                  className="btn-bulk-action-icon btn-bulk-action-icon--delete gf-trash-animated"
                  onClick={bulkSelect.onEnterDelete}
                  title="Excluir em Lote"
                >
                  <TrashAnimatedIcon size={18} />
                </button>
              )}
            </>
          )}

          {/* Ações customizadas (modo list apenas) */}
          {isListMode && children}

          {/* Botão Add - sempre mais à direita, apenas modo list e quando NÃO está em bulk select */}
          {isListMode && onAdd && canAdd && !bulkSelect?.active && (
            <button className="btn-add plus-animated" onClick={onAdd}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <defs>
                  <linearGradient
                    id="addPlusG"
                    gradientUnits="userSpaceOnUse"
                    x1="3"
                    y1="0"
                    x2="21"
                    y2="0"
                  >
                    <stop offset="0%" stopColor="#60a5fa" />
                    <stop offset="100%" stopColor="#154680" />
                  </linearGradient>
                </defs>
                <g className="plus-h" stroke="url(#addPlusG)">
                  <path d="M5 12h14" />
                </g>
                <g className="plus-v" stroke="url(#addPlusG)">
                  <path d="M12 5v14" />
                </g>
              </svg>
              <span className="btn-text">{addLabel}</span>
            </button>
          )}
        </div>

        {/* === MODO DRAWER: Ações específicas === */}
        {isDrawerMode && isEditing && (
          <>
            {/* Cancelar */}
            <button className="btn-add undo-animated" onClick={onCancel} disabled={saving}>
              <UndoAnimatedIcon size={15} />
              <span className="btn-text">Cancelar</span>
            </button>
            {/* Salvar */}
            {awaitingConnection ? (
              <button className="btn-add btn-awaiting-connection" disabled aria-busy="true">
                <span className="reconnecting-spinner reconnecting-spinner--brand" aria-hidden="true" />
                <span className="btn-text">Aguardando conexão</span>
              </button>
            ) : (
              <button className="btn-add save-animated" onClick={onSave} disabled={saving}>
                <SaveAnimatedIcon size={16} />
                <span className="btn-text">{saving ? 'Salvando...' : 'Salvar'}</span>
              </button>
            )}
          </>
        )}

        {isDrawerMode && !isEditing && (
          <>
            {/* Ações customizadas (antes do botão editar) */}
            {children}
            {/* Editar */}
            {onEdit && canEdit && (
              <button className="btn-edit-icon gf-pen-animated" onClick={onEdit} title="Editar">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <defs>
                    <linearGradient
                      id="epA"
                      gradientUnits="userSpaceOnUse"
                      x1="3"
                      y1="0"
                      x2="21"
                      y2="0"
                    >
                      <stop offset="0%" stopColor="#60a5fa" />
                      <stop offset="100%" stopColor="#154680" />
                    </linearGradient>
                  </defs>
                  <g className="pen-square" stroke="url(#epA)">
                    <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  </g>
                  <g className="pen-tip" stroke="url(#epA)">
                    <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" />
                  </g>
                </svg>
              </button>
            )}
            {/* Excluir */}
            {onDelete && canDelete && (
              <button
                className="btn-delete-icon gf-trash-animated"
                onClick={onDelete}
                title="Excluir"
              >
                <TrashAnimatedIcon size={18} />
              </button>
            )}
            {/* Fechar (X) - só em modo view */}
            {onClose && (
              <button className="btn-close" onClick={onClose} title="Fechar">
                <X size={20} />
              </button>
            )}
          </>
        )}

        {/* === MODO DETAILS: Ações específicas === */}
        {isDetailsMode && (
          <>
            {/* Ações customizadas (antes dos botões padrão) */}
            {children}
            {/* Editar */}
            {onEdit && canEdit && (
              <button className="btn-edit-icon gf-pen-animated" onClick={onEdit} title="Editar">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <defs>
                    <linearGradient
                      id="epB"
                      gradientUnits="userSpaceOnUse"
                      x1="3"
                      y1="0"
                      x2="21"
                      y2="0"
                    >
                      <stop offset="0%" stopColor="#60a5fa" />
                      <stop offset="100%" stopColor="#154680" />
                    </linearGradient>
                  </defs>
                  <g className="pen-square" stroke="url(#epB)">
                    <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  </g>
                  <g className="pen-tip" stroke="url(#epB)">
                    <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" />
                  </g>
                </svg>
              </button>
            )}
            {/* Excluir */}
            {onDelete && canDelete && (
              <button
                className="btn-delete-icon gf-trash-animated"
                onClick={onDelete}
                title="Excluir"
              >
                <TrashAnimatedIcon size={18} />
              </button>
            )}
          </>
        )}
      </div>
    </header>
  );
};

export default GenericHeader;
